/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    BarChart3,
    BookOpen,
    Building2,
    Bug,
    CalendarRange,
    CircleDollarSign,
    CircleGauge,
    FileText,
    HardDrive,
    LayoutDashboard,
    LayoutTemplate,
    Languages,
    LineChart,
    List,
    LogIn,
    Network,
    Settings2,
    TriangleAlert,
    Users,
} from 'lucide-react';

/* Import types */
import type { ActiveTab } from '@/types/ui';

/* Import configuration */
import { isDashboardPagePath, isSystemDashboardPath } from '@/config/appPageLayout';
import { APP_ROUTES, getActiveTabFromPathname } from '@/config/appRoutes';
import { OVERVIEW_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { getLayoutSectionByPathname, type LayoutSectionConfig } from '@/config/layoutSections';

/* Import components */
import type { NavbarMenuEntry } from '@/components/layout/AppNavbar';
import type { DashboardBreadcrumbItem } from '@/components/layout/DashboardBreadcrumb';

/* Import libraries */
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

type Translate = (key: string, values?: Record<string, number | string>) => string;

export const APP_ROUTE_TABS: Record<
    'dashboardNavigation' | 'debug' | 'demo' | 'tools' | 'exampleDashboard' | 'publicInfo',
    ActiveTab[]
> = {
    dashboardNavigation: [
        'anomalies',
        'statuses',
        'systems',
        'clients',
        'pricetrafficlight',
        'pvprediction',
        'waterloss',
        'baseload',
        'heatingenergy',
    ],
    debug: ['debugoverview', 'debug', 'errorlog'],
    demo: [
        'demo',
        'plotly',
        'chartdefinitions',
        'buildingstatistics',
        'timeseries',
        'currentvalue',
        'intervalvalues',
        'configuration',
        'allconfigurations',
        'markdown',
        'mermaid',
        'plotlyjson',
        'logindemo',
    ],
    tools: [
        'tools',
        'api',
        'layouts',
        'componentoverview',
        'exampledashboards',
        'kpis',
        'sensorsdevices',
        'devicedefinitions',
        'translations',
    ],
    exampleDashboard: ['overview', 'statistics', 'thermalconflict'],
    publicInfo: ['contact', 'imprint', 'versions'],
};

interface AppPageContextOptions {
    pathname: string;
    t: Translate;
}

export interface AppPageContext {
    activeTab: ActiveTab;
    baseLoadDetailKey: string | null;
    currentPageBreadcrumbItems: DashboardBreadcrumbItem[];
    currentPageTitle: string;
    heatingEnergyDetailKey: string | null;
    waterLossDetailKey: string | null;
    isAnomaliesOverviewPage: boolean;
    isBaseLoadOverviewPage: boolean;
    isClientsPage: boolean;
    isDashboardMenuActive: boolean;
    isDashboardPage: boolean;
    isDebugMenuActive: boolean;
    isDemoMenuActive: boolean;
    isHeatingEnergyOverviewPage: boolean;
    isLoginPage: boolean;
    isPublicInfoPage: boolean;
    isPublicRoute: boolean;
    isPvPredictionOverviewPage: boolean;
    isStatusesPage: boolean;
    isSystemDashboardPage: boolean;
    isSystemAnomalyDetailPage: boolean;
    isSystemMetricsDetailPage: boolean;
    isSystemPriceTrafficLightPage: boolean;
    isSystemStatisticsPage: boolean;
    isToolsMenuActive: boolean;
    pvPredictionDetailKey: string | null;
    statusDetailKey: string | null;
    systemDetailKey: string | null;
    clientDetailKey: string | null;
}

interface SystemAnomalyDetail {
    key: string;
    label: string;
    overviewRoute: string;
}

function getDetailKey(pathname: string, route: string): string | null {
    return pathname.startsWith(`${route}/`)
        ? decodeURIComponent(pathname.slice(route.length + 1))
        : null;
}

interface BreadcrumbOptions {
    activeLayoutSection: LayoutSectionConfig | null;
    activeTab: ActiveTab;
    baseLoadDetailKey: string | null;
    clientDetailKey: string | null;
    currentPageTitle: string;
    debugBreadcrumbPageTitles: Partial<Record<ActiveTab, string>>;
    demoBreadcrumbPageTitles: Partial<Record<ActiveTab, string>>;
    heatingEnergyDetailKey: string | null;
    waterLossDetailKey: string | null;
    isSystemMetricsDetailPage: boolean;
    isSystemPriceTrafficLightPage: boolean;
    isSystemStatisticsPage: boolean;
    pvPredictionDetailKey: string | null;
    statusDetailKey: string | null;
    systemDetailKey: string | null;
    systemAnomalyDetail: SystemAnomalyDetail | null;
    t: Translate;
    toolsBreadcrumbPageTitles: Partial<Record<ActiveTab, string>>;
}

function getAnomalyBreadcrumbItems(
    t: Translate,
    label: string,
    route: string,
    detailKey: string | null,
): DashboardBreadcrumbItem[] {
    return [
        { label: t('Anomalies'), to: APP_ROUTES.anomalies },
        { label, ...(detailKey ? { to: route } : {}) },
        ...(detailKey ? [{ label: getSystemBreadcrumbLabel(detailKey) }] : []),
    ];
}

function getSystemBreadcrumbLabel(systemKey: string): string {
    const systemBreadcrumbCatalog = new DeviceSensorCatalog(OVERVIEW_DEVICE_SENSOR_CONFIG);
    const directName = systemBreadcrumbCatalog.getSensorMetadata(systemKey)?.name;

    if (directName) {
        return directName;
    }

    const systemDevice = systemBreadcrumbCatalog.getDevicesBySystem(systemKey)[0];

    return systemDevice
        ? systemBreadcrumbCatalog.getSensorMetadata(systemDevice)?.name
            ?? systemBreadcrumbCatalog.getDeviceDescription(systemDevice)
            ?? systemKey
        : systemBreadcrumbCatalog.getDeviceDescription(systemKey) ?? systemKey;
}

function getSystemDetailBreadcrumbItems({
    isSystemMetricsDetailPage,
    isSystemPriceTrafficLightPage,
    isSystemStatisticsPage,
    systemDetailKey,
    t,
}: Pick<
    BreadcrumbOptions,
    | 'isSystemMetricsDetailPage'
    | 'isSystemPriceTrafficLightPage'
    | 'isSystemStatisticsPage'
    | 'systemDetailKey'
    | 't'
>): DashboardBreadcrumbItem[] {
    if (!systemDetailKey) {
        return [];
    }

    const detailPageTitle = isSystemMetricsDetailPage
        ? t('Detail')
        : isSystemPriceTrafficLightPage
            ? t('Price Traffic Light')
            : isSystemStatisticsPage
                ? t('Statistics')
                : null;
    const systemRoute = `${APP_ROUTES.systems}/${encodeURIComponent(systemDetailKey)}`;

    return [
        { label: t('Systems'), to: APP_ROUTES.systems },
        {
            label: getSystemBreadcrumbLabel(systemDetailKey),
            ...(detailPageTitle ? { to: systemRoute } : {}),
        },
        ...(detailPageTitle ? [{ label: detailPageTitle }] : []),
    ];
}

function getSystemAnomalyDetailBreadcrumbItems(
    systemKey: string,
    systemAnomalyDetail: SystemAnomalyDetail,
    t: Translate,
): DashboardBreadcrumbItem[] {
    const systemRoute = `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}`;

    return [
        { label: t('Systems'), to: APP_ROUTES.systems },
        { label: getSystemBreadcrumbLabel(systemKey), to: systemRoute },
        { label: systemAnomalyDetail.label, to: systemAnomalyDetail.overviewRoute },
        { label: systemAnomalyDetail.key },
    ];
}

function getSystemAnomalyDetail(
    systemKey: string,
    routeSegment: string | undefined,
    detailKey: string,
    t: Translate,
): SystemAnomalyDetail | null {
    const anomaly = {
        'base-load': { label: t('Base Load'), queryValue: 'bl' },
        'heating-energy': { label: t('Heating Energy'), queryValue: 'heating_energy' },
        'pv-prediction': { label: t('PV Prediction'), queryValue: 'pv' },
        water: { label: t('Water loss'), queryValue: 'water_bl' },
    }[routeSegment ?? ''];

    if (!anomaly) {
        return null;
    }

    return {
        key: decodeURIComponent(detailKey),
        label: anomaly.label,
        overviewRoute: `${APP_ROUTES.systems}/${encodeURIComponent(systemKey)}/detail?anomaly=${anomaly.queryValue}`,
    };
}

function getBreadcrumbItems(options: BreadcrumbOptions): DashboardBreadcrumbItem[] {
    const {
        activeLayoutSection,
        activeTab,
        baseLoadDetailKey,
        clientDetailKey,
        currentPageTitle,
        debugBreadcrumbPageTitles,
        demoBreadcrumbPageTitles,
        heatingEnergyDetailKey,
        waterLossDetailKey,
        pvPredictionDetailKey,
        statusDetailKey,
        systemDetailKey,
        systemAnomalyDetail,
        t,
        toolsBreadcrumbPageTitles,
    } = options;

    if (activeTab === 'pvprediction') {
        return getAnomalyBreadcrumbItems(t, t('PV Prediction'), APP_ROUTES.pvprediction, pvPredictionDetailKey);
    }

    if (activeTab === 'baseload') {
        return getAnomalyBreadcrumbItems(t, t('Base Load'), APP_ROUTES.baseload, baseLoadDetailKey);
    }

    if (activeTab === 'heatingenergy') {
        return getAnomalyBreadcrumbItems(t, t('Heating Energy'), APP_ROUTES.heatingenergy, heatingEnergyDetailKey);
    }

    if (activeTab === 'waterloss') {
        return getAnomalyBreadcrumbItems(t, t('Water loss'), APP_ROUTES.waterloss, waterLossDetailKey);
    }

    if (activeTab === 'layouts' && activeLayoutSection) {
        return [
            { label: t('Tools'), to: APP_ROUTES.tools },
            { label: t('Layouts'), to: APP_ROUTES.layouts },
            { label: t(activeLayoutSection.label) },
        ];
    }

    const demoPageTitle = demoBreadcrumbPageTitles[activeTab];

    if (demoPageTitle) {
        return [
            { label: t('Demo'), to: APP_ROUTES.demo },
            { label: demoPageTitle },
        ];
    }

    const toolsPageTitle = toolsBreadcrumbPageTitles[activeTab];

    if (toolsPageTitle) {
        return [
            { label: t('Tools'), to: APP_ROUTES.tools },
            { label: toolsPageTitle },
        ];
    }

    const debugPageTitle = debugBreadcrumbPageTitles[activeTab];

    if (debugPageTitle) {
        return [
            { label: t('Debug'), to: APP_ROUTES.debugoverview },
            { label: debugPageTitle },
        ];
    }

    if (activeTab === 'exampledashboards') {
        return [
            { label: t('Tools'), to: APP_ROUTES.tools },
            { label: t('Example Dashboards') },
        ];
    }

    if (APP_ROUTE_TABS.exampleDashboard.includes(activeTab)) {
        return [
            { label: t('Tools'), to: APP_ROUTES.tools },
            { label: t('Example Dashboards'), to: APP_ROUTES.exampledashboards },
            { label: currentPageTitle },
        ];
    }

    if (activeTab === 'systems' && systemDetailKey && systemAnomalyDetail) {
        return getSystemAnomalyDetailBreadcrumbItems(systemDetailKey, systemAnomalyDetail, t);
    }

    if (activeTab === 'systems' && systemDetailKey) {
        return getSystemDetailBreadcrumbItems(options);
    }

    if (activeTab === 'statuses' && statusDetailKey) {
        return [
            { label: t('Statuses'), to: APP_ROUTES.statuses },
            { label: t(statusDetailKey.charAt(0).toUpperCase() + statusDetailKey.slice(1)) },
        ];
    }

    if (activeTab === 'clients' && clientDetailKey) {
        return [
            { label: t('Customers'), to: APP_ROUTES.clients },
            { label: clientDetailKey },
        ];
    }

    return [{ label: currentPageTitle }];
}

export function getAppPageContext({ pathname, t }: AppPageContextOptions): AppPageContext {
    const activeTab = getActiveTabFromPathname(pathname) ?? 'overview';
    const activeLayoutSection = getLayoutSectionByPathname(pathname);
    const pvPredictionDetailKey = getDetailKey(pathname, APP_ROUTES.pvprediction);
    const baseLoadDetailKey = getDetailKey(pathname, APP_ROUTES.baseload);
    const heatingEnergyDetailKey = getDetailKey(pathname, APP_ROUTES.heatingenergy);
    const waterLossDetailKey = getDetailKey(pathname, APP_ROUTES.waterloss);
    const statusDetailKey = getDetailKey(pathname, APP_ROUTES.statuses);
    const clientDetailKey = getDetailKey(pathname, APP_ROUTES.clients);
    const systemPathParts = pathname.startsWith(`${APP_ROUTES.systems}/`)
        ? pathname.slice(APP_ROUTES.systems.length + 1).split('/')
        : [];
    const systemDetailKey = systemPathParts[0]
        ? decodeURIComponent(systemPathParts[0])
        : null;
    const isSystemMetricsDetailPage = systemPathParts[1] === 'detail';
    const isSystemPriceTrafficLightPage = systemPathParts[1] === 'price-traffic-light';
    const isSystemStatisticsPage = systemPathParts[1] === 'statistics';
    const systemAnomalyDetail = systemDetailKey && systemPathParts[2]
        ? getSystemAnomalyDetail(systemDetailKey, systemPathParts[1], systemPathParts[2], t)
        : null;
    const isSystemAnomalyDetailPage = systemAnomalyDetail !== null;
    const isPvPredictionOverviewPage = pathname === APP_ROUTES.pvprediction;
    const isBaseLoadOverviewPage = pathname === APP_ROUTES.baseload;
    const isHeatingEnergyOverviewPage = pathname === APP_ROUTES.heatingenergy;
    const isAnomaliesOverviewPage = pathname === APP_ROUTES.anomalies;
    const isStatusesPage = pathname === APP_ROUTES.statuses || pathname.startsWith(`${APP_ROUTES.statuses}/`);
    const isSystemDashboardPage = isSystemDashboardPath(pathname);
    const isClientsPage = pathname === APP_ROUTES.clients || pathname.startsWith(`${APP_ROUTES.clients}/`);
    const isLoginPage = pathname === APP_ROUTES.login;
    const isPublicInfoPage = APP_ROUTE_TABS.publicInfo.includes(activeTab);
    const isPublicRoute = isLoginPage || isPublicInfoPage;
    const isDashboardPage = isDashboardPagePath(pathname);
    const activePageTitle = {
        home: t('Home'),
        overview: t('Overview'),
        statistics: t('Statistics'),
        anomalies: t('Anomalies'),
        statuses: t('Statuses'),
        systems: t('Systems'),
        clients: t('Customers'),
        pricetrafficlight: t('Price Traffic Light'),
        pvprediction: t('PV Prediction'),
        baseload: t('Base Load'),
        heatingenergy: t('Heating Energy'),
        waterloss: t('Water loss'),
        thermalconflict: t('Thermal Conflict'),
        contact: t('Contact'),
        imprint: t('Imprint'),
        versions: t('Versions'),
        login: t('Login'),
        plotly: t('Plotly Demo'),
        chartdefinitions: t('Chart Definitions'),
        buildingstatistics: t('Building statistics'),
        timeseries: t('Time Series Demo'),
        currentvalue: t('Current Value Demo'),
        intervalvalues: t('Interval Values Demo'),
        configuration: t('Configuration Demo'),
        allconfigurations: t('All configurations'),
        markdown: t('Markdown'),
        mermaid: t('Mermaid'),
        plotlyjson: t('Plotly'),
        api: t('API'),
        debugoverview: t('Debug'),
        debug: t('Debug'),
        errorlog: t('Error Log'),
        logindemo: t('Login Demo'),
        demo: t('Demo'),
        componentoverview: t('Component Overview'),
        exampledashboards: t('Example Dashboards'),
        layouts: t('Layouts'),
        kpis: t('KPIs'),
        sensorsdevices: t('Sensors / Devices'),
        devicedefinitions: t('Device Definitions'),
        translations: t('Translations'),
        tools: t('Tools'),
    } satisfies Record<ActiveTab, string>;
    const currentPageTitle = activeTab === 'pvprediction'
        ? pvPredictionDetailKey
            ? t('PV Prediction {key}', { key: pvPredictionDetailKey })
            : t('PV Prediction Overview')
        : activeTab === 'baseload'
            ? baseLoadDetailKey
                ? t('Base Load {key}', { key: baseLoadDetailKey })
                : t('Base Load Overview')
                : activeTab === 'heatingenergy'
                    ? heatingEnergyDetailKey
                        ? t('Heating Energy {key}', { key: heatingEnergyDetailKey })
                        : t('Heating Energy Overview')
                    : activeTab === 'waterloss'
                        ? waterLossDetailKey
                            ? t('Water loss {key}', { key: waterLossDetailKey })
                            : t('Water loss overview')
                    : activeTab === 'layouts' && activeLayoutSection
                    ? t(activeLayoutSection.label)
                    : activePageTitle[activeTab];
    const demoBreadcrumbPageTitles: Partial<Record<ActiveTab, string>> = {
        logindemo: t('Login Demo'),
        plotly: t('Plotly'),
        chartdefinitions: t('Chart Definitions'),
        buildingstatistics: t('Building statistics'),
        timeseries: t('Time Series'),
        currentvalue: t('Current Value'),
        intervalvalues: t('Interval Values'),
        configuration: t('Configuration'),
        allconfigurations: t('All configurations'),
        markdown: t('Markdown'),
        mermaid: t('Mermaid'),
        plotlyjson: t('Plotly'),
    };
    const toolsBreadcrumbPageTitles: Partial<Record<ActiveTab, string>> = {
        api: t('API'),
        layouts: t('Layouts'),
        componentoverview: t('Component Overview'),
        kpis: t('KPIs'),
        sensorsdevices: t('Sensors / Devices'),
        devicedefinitions: t('Device Definitions'),
        translations: t('Translations'),
    };
    const debugBreadcrumbPageTitles: Partial<Record<ActiveTab, string>> = {
        debug: t('API Calls'),
        errorlog: t('Error Log'),
    };
    const currentPageBreadcrumbItems = getBreadcrumbItems({
        activeLayoutSection,
        activeTab,
        baseLoadDetailKey,
        clientDetailKey,
        currentPageTitle,
        debugBreadcrumbPageTitles,
        demoBreadcrumbPageTitles,
        heatingEnergyDetailKey,
        waterLossDetailKey,
        isSystemMetricsDetailPage,
        isSystemPriceTrafficLightPage,
        isSystemStatisticsPage,
        pvPredictionDetailKey,
        statusDetailKey,
        systemDetailKey,
        systemAnomalyDetail,
        t,
        toolsBreadcrumbPageTitles,
    });


    return {
        activeTab,
        baseLoadDetailKey,
        clientDetailKey,
        currentPageBreadcrumbItems,
        currentPageTitle,
        heatingEnergyDetailKey,
        waterLossDetailKey,
        isAnomaliesOverviewPage,
        isBaseLoadOverviewPage,
        isClientsPage,
        isDashboardMenuActive: APP_ROUTE_TABS.dashboardNavigation.includes(activeTab) || activeTab === 'pricetrafficlight',
        isDashboardPage,
        isDebugMenuActive: APP_ROUTE_TABS.debug.includes(activeTab),
        isDemoMenuActive: APP_ROUTE_TABS.demo.includes(activeTab),
        isToolsMenuActive: APP_ROUTE_TABS.tools.includes(activeTab) || APP_ROUTE_TABS.exampleDashboard.includes(activeTab),
        isHeatingEnergyOverviewPage,
        isLoginPage,
        isPublicInfoPage,
        isPublicRoute,
        isPvPredictionOverviewPage,
        isStatusesPage,
        isSystemDashboardPage,
        isSystemAnomalyDetailPage,
        isSystemMetricsDetailPage,
        isSystemPriceTrafficLightPage,
        isSystemStatisticsPage,
        pvPredictionDetailKey,
        statusDetailKey,
        systemDetailKey,
    };
}

interface AppNavigationOptions {
    debugEntryCount: number;
    hasSession: boolean;
    t: Translate;
}

export interface AppNavigation {
    dashboardMenuItems: NavbarMenuEntry[];
    debugMenuItems: NavbarMenuEntry[];
    demoMenuItems: NavbarMenuEntry[];
    toolsMenuItems: NavbarMenuEntry[];
}

export function createAppNavigation({ debugEntryCount, hasSession, t }: AppNavigationOptions): AppNavigation {
    return {
        dashboardMenuItems: [
            { kind: 'caption', label: t('Overviews') },
            { kind: 'item', label: t('Anomalies'), route: APP_ROUTES.anomalies, enabled: hasSession, icon: TriangleAlert },
            { kind: 'item', label: t('Statuses'), route: APP_ROUTES.statuses, enabled: hasSession, icon: CircleGauge },
            { kind: 'item', label: t('Systems'), route: APP_ROUTES.systems, enabled: hasSession, icon: Building2 },
            { kind: 'item', label: t('Customers'), route: APP_ROUTES.clients, enabled: hasSession, icon: Users },
            { kind: 'divider' },
            { kind: 'caption', label: t('Market & Prices') },
            { kind: 'item', label: t('Price Traffic Light'), route: APP_ROUTES.pricetrafficlight, enabled: hasSession, icon: CircleDollarSign },
        ],
        demoMenuItems: [
            { kind: 'caption', label: t('API Demos') },
            { kind: 'item', label: `1. ${t('Login')}`, route: APP_ROUTES.logindemo, enabled: hasSession, icon: LogIn },
            { kind: 'item', label: `2. ${t('Configuration')}`, route: APP_ROUTES.configuration, enabled: hasSession, icon: Settings2 },
            { kind: 'item', label: `3. ${t('Time Series')}`, route: APP_ROUTES.timeseries, enabled: hasSession, icon: BarChart3 },
            { kind: 'item', label: `4. ${t('Current Value')}`, route: APP_ROUTES.currentvalue, enabled: hasSession, icon: List },
            { kind: 'item', label: `5. ${t('Interval Values')}`, route: APP_ROUTES.intervalvalues, enabled: hasSession, icon: CalendarRange },
            { kind: 'item', label: `6. ${t('Plotly')}`, route: APP_ROUTES.plotly, enabled: hasSession, icon: LineChart },
            { kind: 'item', label: `7. ${t('Chart Definitions')}`, route: APP_ROUTES.chartdefinitions, enabled: hasSession, icon: LineChart },
            { kind: 'item', label: `8. ${t('Building statistics')}`, route: APP_ROUTES.buildingstatistics, enabled: hasSession, icon: BarChart3 },
            { kind: 'item', label: `9. ${t('All configurations')}`, route: APP_ROUTES.allconfigurations, enabled: hasSession, icon: Settings2 },
            { kind: 'divider' },
            { kind: 'caption', label: t('Renderer Demos') },
            { kind: 'item', label: `1. ${t('Markdown')}`, route: APP_ROUTES.markdown, enabled: hasSession, icon: FileText },
            { kind: 'item', label: `2. ${t('Mermaid')}`, route: APP_ROUTES.mermaid, enabled: hasSession, icon: Network },
            { kind: 'item', label: `3. ${t('Plotly')}`, route: APP_ROUTES.plotlyjson, enabled: hasSession, icon: LineChart },
        ],
        toolsMenuItems: [
            { kind: 'caption', label: t('Reference & Tools') },
            { kind: 'item', label: `1. ${t('API')}`, route: APP_ROUTES.api, enabled: hasSession, icon: BookOpen },
            { kind: 'item', label: `2. ${t('Example Dashboards')}`, route: APP_ROUTES.exampledashboards, enabled: hasSession, icon: LayoutDashboard },
            { kind: 'item', label: `3. ${t('Device Definitions')}`, route: APP_ROUTES.devicedefinitions, enabled: hasSession, icon: FileText },
            { kind: 'item', label: `4. ${t('Component Overview')}`, route: APP_ROUTES.componentoverview, enabled: hasSession, icon: Network },
            { kind: 'item', label: `5. ${t('KPIs')}`, route: APP_ROUTES.kpis, enabled: hasSession, icon: BarChart3 },
            { kind: 'item', label: `6. ${t('Layouts')}`, route: APP_ROUTES.layouts, enabled: hasSession, icon: LayoutTemplate },
            { kind: 'item', label: `7. ${t('Sensors / Devices')}`, route: APP_ROUTES.sensorsdevices, enabled: hasSession, icon: HardDrive },
            { kind: 'item', label: `8. ${t('Translations')}`, route: APP_ROUTES.translations, enabled: hasSession, icon: Languages },
        ],
        debugMenuItems: [
            { kind: 'caption', label: t('API Requests') },
            { kind: 'item', label: t('API Calls'), route: APP_ROUTES.debug, enabled: hasSession && debugEntryCount > 0, icon: Bug },
            { kind: 'divider' },
            { kind: 'caption', label: t('Error Diagnostics') },
            { kind: 'item', label: t('Error Log'), route: APP_ROUTES.errorlog, enabled: hasSession, icon: TriangleAlert },
        ],
    };
}
